import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const ThemeContext = createContext({ theme: 'corporate', setTheme: () => {}, customTheme: null });

export const THEMES = {
  corporate: { id: 'corporate', name: 'Corporate Steel' },
  midnight: { id: 'midnight', name: 'Midnight Industrial' },
  earth: { id: 'earth', name: 'Warm Earth' },
  arctic: { id: 'arctic', name: 'Arctic Minimal' },
  luxe: { id: 'luxe', name: 'Bold Luxe' },
  custom: { id: 'custom', name: 'Custom Theme' },
};

function applyCustomVars(cfg) {
  if (!cfg) return;
  const root = document.documentElement;
  root.style.setProperty('--color-ink', cfg.textColor || '#1d1d1d');
  root.style.setProperty('--color-ink-light', cfg.textColor || '#333');
  root.style.setProperty('--color-blue', cfg.accentColor || '#3B9BD9');
  root.style.setProperty('--color-blue-light', cfg.accentColor || '#3B9BD9');
  root.style.setProperty('--color-surface', cfg.surfaceColor || '#1B2838');
  root.style.setProperty('--color-mist', cfg.bgColor || '#F5F7FA');
  root.style.setProperty('--color-panel', cfg.bgColor || '#fafafa');
  root.style.setProperty('--th-nav-bg', cfg.isDark ? `${cfg.surfaceColor}ee` : `${cfg.bgColor}ee`);
  root.style.setProperty('--th-nav-text', cfg.textColor || '#1d1d1d');
  root.style.setProperty('--th-footer-bg', cfg.isDark ? cfg.surfaceColor : cfg.bgColor);
  root.style.setProperty('--th-section-bg', cfg.bgColor || '#fff');
  root.style.setProperty('--th-section-alt', cfg.isDark ? cfg.surfaceColor : '#f9fafb');
  root.style.setProperty('--th-card-bg', cfg.isDark ? cfg.surfaceColor : '#fff');
  root.style.setProperty('--th-card-border', cfg.isDark ? '#333' : '#e5e7eb');
  root.style.setProperty('--th-btn-radius', cfg.btnRadius || '2px');
  root.style.setProperty('--th-radius', cfg.borderRadius || '4px');
  root.style.setProperty('--th-input-bg', cfg.isDark ? cfg.surfaceColor : '#fff');
  root.style.setProperty('--th-input-border', cfg.isDark ? '#444' : '#d1d5db');
  document.body.style.background = cfg.bgColor || '#fff';
  document.body.style.color = cfg.textColor || '#1d1d1d';
  document.body.style.fontFamily = cfg.bodyFont || '"Google Sans", sans-serif';
}

function clearCustomVars() {
  const root = document.documentElement;
  const props = ['--color-ink','--color-ink-light','--color-blue','--color-blue-light','--color-surface','--color-mist','--color-panel','--th-nav-bg','--th-nav-text','--th-footer-bg','--th-section-bg','--th-section-alt','--th-card-bg','--th-card-border','--th-btn-radius','--th-radius','--th-input-bg','--th-input-border'];
  props.forEach(p => root.style.removeProperty(p));
  document.body.style.background = '';
  document.body.style.color = '';
  document.body.style.fontFamily = '';
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState('corporate');
  const [customTheme, setCustomTheme] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/settings/public/site_settings`).then(r => {
      const t = r.data?.theme || 'corporate';
      const ct = r.data?.custom_theme || null;
      if (t === 'custom' && ct) {
        setCustomTheme(ct);
        setThemeState('custom');
      } else if (THEMES[t]) {
        setThemeState(t);
      }
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    if (theme === 'custom' && customTheme) {
      applyCustomVars(customTheme);
    } else {
      clearCustomVars();
    }
  }, [theme, customTheme]);

  const setTheme = (t, customCfg) => {
    if (t === 'custom' && customCfg) {
      setCustomTheme(customCfg);
      setThemeState('custom');
      document.documentElement.setAttribute('data-theme', 'custom');
      applyCustomVars(customCfg);
    } else if (THEMES[t]) {
      setThemeState(t);
      setCustomTheme(null);
      document.documentElement.setAttribute('data-theme', t);
      clearCustomVars();
    }
  };

  if (!loaded) return null;

  return (
    <ThemeContext.Provider value={{ theme, setTheme, customTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
export { applyCustomVars, clearCustomVars };
